import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import axios from 'axios';
import { AlertTriangle, FileX, Clock, ArrowRight, X, RefreshCw, CheckCircle, Loader2, GraduationCap } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { fetchMyLearningEligibility } from '@/pages/learning/hooks/useLearningApi';
import { complianceUploadEntry } from '@/utils/complianceUploadEntry';

const API = process.env.REACT_APP_BACKEND_URL || '';

/**
 * Persistent warning banner shown when user has compliance issues:
 * 1. Documents NOT uploaded (blocks_login = true) - Red banner "Access Restricted"
 * 2. Documents uploaded but pending approval - Amber banner "Pending Review"
 * 
 * Banner remains visible until ALL required documents are approved.
 */
const ComplianceWarningBanner = ({ user }) => {
  const location = useLocation();
  const [complianceStatus, setComplianceStatus] = useState(null);
  const [learningBlockers, setLearningBlockers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [minimized, setMinimized] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [userRoles, setUserRoles] = useState([]);

  // Only show for employees
  const isEmployee = user?.role === 'employee';

  // Roles that don't require rostering (administrative/coordination roles)
  const NON_ROSTERING_ROLES = [
    'Support Coordinator - Level 1 (Support Connection)',
    'Support Coordinator - Level 2 (Coordination of Supports)',
    'Support Coordinator - Level 3 (Specialist Support Coordination)',
    'HR Officer / HR Manager',
    'Compliance Officer',
    'Finance Officer / Accounts Manager',
    'Payroll Officer',
    'IT Administrator / System Administrator',
    'Administrative Assistant',
    'Quality Manager / Quality Officer',
    'Rostering Coordinator / Scheduling Coordinator',
  ];

  // Check if user has a non-rostering role
  const hasNonRosteringRole = userRoles.some(role => 
    NON_ROSTERING_ROLES.some(nonRosterRole => 
      role.name?.toLowerCase().includes(nonRosterRole.toLowerCase().split(' ')[0]) ||
      nonRosterRole.toLowerCase().includes(role.name?.toLowerCase())
    )
  );

  useEffect(() => {
    if (isEmployee) {
      fetchComplianceStatus();
      fetchUserRoles();
    }
  }, [isEmployee]);

  const fetchUserRoles = async () => {
    try {
      const token = localStorage.getItem('access_token');
      const res = await axios.get(`${API}/api/rbac/permissions/my`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUserRoles(res.data.roles || []);
    } catch (error) {
      console.error('Failed to fetch user roles:', error);
    }
  };

  const fetchComplianceStatus = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('access_token');
      const headers = { Authorization: `Bearer ${token}` };
      const [statusRes, eligibilityRes] = await Promise.all([
        axios.get(`${API}/api/compliance/my-status`, { headers }),
        fetchMyLearningEligibility('login').catch(() => ({ blockers: [] })),
      ]);
      setComplianceStatus(statusRes.data.status);
      setLearningBlockers(
        (eligibilityRes?.blockers || []).filter((b) => b.source === 'learning_enrollment')
      );
    } catch (error) {
      console.error('Failed to fetch compliance status:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    await fetchComplianceStatus();
    setRefreshing(false);
    
    // If fully compliant, reload page to update user state
    if (complianceStatus?.is_compliant) {
      window.location.reload();
    }
  };

  // Don't show for non-employees or while loading
  if (!isEmployee || loading) {
    return null;
  }

  // Don't show if fully compliant (all approved, none missing, none expired) and no learning blockers
  if (complianceStatus?.is_compliant && learningBlockers.length === 0) {
    return null;
  }

  const missingDocs = complianceStatus?.missing_documents || [];
  const expiredDocs = complianceStatus?.expired_documents || [];
  const overdueLearning = learningBlockers.filter(
    (b) => b.code === 'learning_overdue' || b.code === 'learning_incomplete'
  );
  const totalRequired = complianceStatus?.total_required || 0;
  const totalApproved = complianceStatus?.total_approved || 0;
  const totalUploaded = complianceStatus?.total_uploaded || 0;
  const pendingReview = totalUploaded - totalApproved - (complianceStatus?.total_expired || 0);
  
  // Determine banner type:
  // - RED: Has missing or expired documents (blocks_login)
  // - AMBER: All uploaded but pending approval
  const hasMissingOrExpired = missingDocs.length > 0 || expiredDocs.length > 0 || overdueLearning.length > 0;
  const hasPendingReview = pendingReview > 0 && !hasMissingOrExpired;
  
  // If nothing to show, don't render
  if (!hasMissingOrExpired && !hasPendingReview) {
    return null;
  }

  // Customize message based on role type
  const getRosteringMessage = () => {
    if (hasNonRosteringRole) {
      return 'Please complete your compliance documents.';
    }
    return 'You cannot be rostered until all documents are uploaded and approved';
  };

  const getPendingMessage = () => {
    if (hasNonRosteringRole) {
      return 'Your documents are being reviewed.';
    }
    return 'Your documents are being reviewed. You cannot be rostered until approved.';
  };

  const bannerStyles = hasMissingOrExpired 
    ? {
        bg: 'from-red-600 to-red-700',
        hoverBg: 'hover:bg-red-500/30',
        borderColor: 'border-red-500/30',
        buttonBg: 'bg-white text-red-700 hover:bg-red-50',
        pillBg: 'bg-red-600',
        title: hasNonRosteringRole ? 'Compliance Documents Required' : 'Access Restricted',
        subtitle: getRosteringMessage()
      }
    : {
        bg: 'from-amber-500 to-amber-600',
        hoverBg: 'hover:bg-amber-400/30',
        borderColor: 'border-amber-400/30',
        buttonBg: 'bg-white text-amber-700 hover:bg-amber-50',
        pillBg: 'bg-amber-500',
        title: 'Documents Pending Review',
        subtitle: getPendingMessage()
      };

  // Minimized view - just a small indicator
  if (minimized) {
    return (
      <div 
        className="fixed bottom-4 right-4 z-50 cursor-pointer"
        onClick={() => setMinimized(false)}
      >
        <div className={`${bannerStyles.pillBg} text-white px-4 py-2 rounded-full shadow-lg flex items-center gap-2 hover:opacity-90 transition-opacity`}>
          {hasMissingOrExpired ? (
            <AlertTriangle className="h-4 w-4" />
          ) : (
            <Loader2 className="h-4 w-4 animate-spin" />
          )}
          <span className="text-sm font-medium">
            {hasMissingOrExpired 
              ? `${missingDocs.length + expiredDocs.length + overdueLearning.length} Compliance Issue${missingDocs.length + expiredDocs.length + overdueLearning.length !== 1 ? 's' : ''}`
              : `${pendingReview} Pending Review`
            }
          </span>
        </div>
      </div>
    );
  }

  return (
    <div className={`bg-gradient-to-r ${bannerStyles.bg} text-white px-4 py-3 shadow-md relative z-50`}>
      <div className="max-w-7xl mx-auto">
        <div className="flex items-start sm:items-center justify-between gap-4 flex-col sm:flex-row">
          {/* Main Warning */}
          <div className="flex items-start gap-3 flex-1">
            {hasMissingOrExpired ? (
              <AlertTriangle className="h-5 w-5 mt-0.5 flex-shrink-0" />
            ) : (
              <Loader2 className="h-5 w-5 mt-0.5 flex-shrink-0 animate-spin" />
            )}
            <div className="flex-1">
              <p className="font-semibold text-sm sm:text-base">
                {bannerStyles.title}
              </p>
              <p className="text-white/80 text-xs sm:text-sm mt-0.5">
                {hasMissingOrExpired ? (
                  <>
                    {missingDocs.length > 0 && (
                      <span className="inline-flex items-center gap-1 mr-3">
                        <FileX className="h-3 w-3" />
                        {missingDocs.length} missing
                      </span>
                    )}
                    {expiredDocs.length > 0 && (
                      <span className="inline-flex items-center gap-1 mr-3">
                        <Clock className="h-3 w-3" />
                        {expiredDocs.length} expired
                      </span>
                    )}
                    {overdueLearning.length > 0 && (
                      <span className="inline-flex items-center gap-1 mr-3">
                        <GraduationCap className="h-3 w-3" />
                        {overdueLearning.length} overdue training
                      </span>
                    )}
                  </>
                ) : (
                  <span className="inline-flex items-center gap-1">
                    <CheckCircle className="h-3 w-3" />
                    {pendingReview} document{pendingReview !== 1 ? 's' : ''} awaiting approval
                  </span>
                )}
                <span className="ml-2 opacity-80">• {bannerStyles.subtitle}</span>
              </p>
            </div>
          </div>

          {/* Actions */}
          <div className="flex items-center gap-2 w-full sm:w-auto">
            <Button
              asChild
              size="sm"
              variant="secondary"
              className={`flex-1 sm:flex-none ${bannerStyles.buttonBg}`}
            >
              <Link
                to={complianceUploadEntry(location.pathname, {
                  learningOnly: overdueLearning.length > 0 && missingDocs.length === 0 && expiredDocs.length === 0,
                }).to}
                onClick={(event) => {
                  const action = complianceUploadEntry(location.pathname, {
                    learningOnly: overdueLearning.length > 0 && missingDocs.length === 0 && expiredDocs.length === 0,
                  });
                  if (action.kind !== 'scroll' || !action.id) return;
                  event.preventDefault();
                  const node = document.getElementById(action.id);
                  if (node) node.scrollIntoView({ behavior: 'smooth', block: 'start' });
                }}
              >
                {overdueLearning.length > 0 && missingDocs.length === 0 && expiredDocs.length === 0
                  ? 'Go to My Learning'
                  : hasMissingOrExpired ? 'Upload Documents' : 'View Status'}
                <ArrowRight className="h-3 w-3 ml-1" />
              </Link>
            </Button>
            
            <Button
              size="sm"
              variant="ghost"
              className={`text-white ${bannerStyles.hoverBg}`}
              onClick={handleRefresh}
              disabled={refreshing}
            >
              <RefreshCw className={`h-4 w-4 ${refreshing ? 'animate-spin' : ''}`} />
            </Button>
            
            <Button
              size="sm"
              variant="ghost"
              className={`text-white ${bannerStyles.hoverBg}`}
              onClick={() => setMinimized(true)}
              title="Minimize banner"
            >
              <X className="h-4 w-4" />
            </Button>
          </div>
        </div>

        {/* Document List - Only show for missing/expired (not pending) */}
        {hasMissingOrExpired && (missingDocs.length + expiredDocs.length + overdueLearning.length) <= 5 && (
          <div className={`mt-2 pt-2 border-t ${bannerStyles.borderColor}`}>
            <div className="flex flex-wrap gap-2">
              {missingDocs.map((doc, i) => (
                <span 
                  key={`missing-${i}`}
                  className="inline-flex items-center gap-1 px-2 py-0.5 bg-white/20 rounded text-xs"
                >
                  <FileX className="h-3 w-3" />
                  {doc}
                </span>
              ))}
              {expiredDocs.map((doc, i) => (
                <span 
                  key={`expired-${i}`}
                  className="inline-flex items-center gap-1 px-2 py-0.5 bg-white/20 rounded text-xs"
                >
                  <Clock className="h-3 w-3" />
                  {doc}
                </span>
              ))}
              {overdueLearning.map((item, i) => (
                <span
                  key={`learning-${i}`}
                  className="inline-flex items-center gap-1 px-2 py-0.5 bg-white/20 rounded text-xs"
                >
                  <GraduationCap className="h-3 w-3" />
                  {item.message}
                </span>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ComplianceWarningBanner;
